import React, { useState } from "react";

const fmtSlotTime = (v) => {
  if (!v) return "—";
  const t = String(v).slice(0, 5);
  return t === "23:59" ? "24:00" : t;
};

// ─── Shared primitives ────────────────────────────────────────────────────────

const STATUS_COLORS = {
  ACTIVE: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  ONGOING: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  SCHEDULED: { bg: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" },
  PENDING: { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
  TERMS_ACCEPTED: { bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
  AGREEMENT_ACCEPTED: { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  ACCEPTED: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  INACTIVE: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  EXPIRED: { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
  ADD: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  MOD: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  DELETE: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
};

const StatusChip = ({ value }) => {
  const c = STATUS_COLORS[value?.toUpperCase()] || {
    bg: "#f1f5f9",
    color: "#475569",
    border: "#cbd5e1",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 10px",
        letterSpacing: 0.3,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {value || "—"}
    </span>
  );
};

const Field = ({ label, value, mono, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: "#94a3b8",
      }}
    >
      {label}
    </span>
    {children ? (
      children
    ) : (
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#1e293b",
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </span>
    )}
  </div>
);

const Section = ({ title, accent, children, gridCols }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: accent || "#64748b",
        padding: "10px 18px",
        borderBottom: "1px solid #f1f5f9",
        background: accent ? `${accent}08` : "#f8fafc",
        borderLeft: accent ? `3px solid ${accent}` : "none",
      }}
    >
      {title}
    </div>
    <div
      style={{
        padding: "14px 18px",
        display: "grid",
        gridTemplateColumns:
          gridCols || "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "12px 20px",
      }}
    >
      {children}
    </div>
  </div>
);

const EmptyState = ({ icon, text }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "48px 24px",
      color: "#94a3b8",
    }}
  >
    <span style={{ fontSize: 36, opacity: 0.4 }}>{icon}</span>
    <span style={{ fontSize: 14 }}>{text}</span>
  </div>
);

const TH = ({ children, style }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.7,
      textTransform: "uppercase",
      color: "#94a3b8",
      padding: "8px 12px",
      background: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </div>
);

const TD = ({ children, chip, center, style }) => (
  <div
    style={{
      fontSize: 12,
      color: "#334155",
      padding: "9px 12px",
      borderBottom: "1px solid #f1f5f9",
      fontVariantNumeric: "tabular-nums",
      textAlign: center ? "center" : "left",
      ...style,
    }}
  >
    {chip ? <StatusChip value={children} /> : (children ?? "—")}
  </div>
);

const TabBar = ({ tabs, active, onSelect }) => (
  <div
    style={{
      display: "flex",
      borderBottom: "2px solid #f1f5f9",
      background: "#fff",
      flexShrink: 0,
    }}
  >
    {tabs.map((t) => {
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "11px 18px",
            border: "none",
            borderBottom: `2px solid ${isActive ? t.accent : "transparent"}`,
            marginBottom: "-2px",
            background: "transparent",
            color: isActive ? t.accent : "#64748b",
            fontWeight: isActive ? 700 : 500,
            cursor: "pointer",
            fontSize: 13,
            transition: "all 0.15s",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {t.label}
          {t.count != null && (
            <span
              style={{
                background: isActive ? `${t.accent}20` : "#f1f5f9",
                color: isActive ? t.accent : "#94a3b8",
                borderRadius: 20,
                padding: "1px 8px",
                fontSize: 11,
                fontWeight: 700,
                transition: "all 0.15s",
              }}
            >
              {t.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// ─── Revision History (shared) ────────────────────────────────────────────────

const RevisionTab = ({ revisionHistory }) => {
  if (!revisionHistory?.length)
    return <EmptyState icon="🕓" text="No revision history found" />;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "100px 1fr 100px 1fr",
          }}
        >
          {["Revision ID", "Date", "Type", "Status"].map((h) => (
            <TH key={h}>{h}</TH>
          ))}
          {revisionHistory.map((rev, i) => (
            <React.Fragment key={i}>
              <TD mono>{rev.revisionId}</TD>
              <TD>{rev.revisionDate}</TD>
              <TD>
                <StatusChip value={rev.revisionType} />
              </TD>
              <TD chip>{rev.status}</TD>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Slots mini-table (shared) ────────────────────────────────────────────────

const SlotsTable = ({ slots, costKey, energyKey }) => {
  if (!slots?.length)
    return (
      <div style={{ fontSize: 12, color: "#94a3b8", padding: 8 }}>No slots</div>
    );
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        overflow: "hidden",
        marginTop: 4,
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 80px 80px 90px 90px 90px",
          }}
        >
          {[
            "Date Start",
            "Date End",
            "Start",
            "End",
            "Qty (MW)",
            costKey === "landingCost" ? "Buy ₹" : "Sell ₹",
            "Rate Type",
          ].map((h) => (
            <TH key={h} style={{ fontSize: 9 }}>
              {h}
            </TH>
          ))}
          {slots.map((s, i) => (
            <React.Fragment key={i}>
              <TD style={{ fontSize: 11 }}>{s.dateStartAt}</TD>
              <TD style={{ fontSize: 11 }}>{s.dateEndAt}</TD>
              <TD style={{ fontSize: 11 }}>{fmtSlotTime(s.slotStartAt)}</TD>
              <TD style={{ fontSize: 11 }}>{fmtSlotTime(s.slotEndAt)}</TD>
              <TD style={{ fontSize: 11 }}>
                {s[energyKey] != null ? `${s[energyKey]}` : "—"}
              </TD>
              <TD style={{ fontSize: 11 }}>
                {s[costKey] != null ? `₹ ${s[costKey]}` : "—"}
              </TD>
              <TD style={{ fontSize: 11 }}>{s.energyRateType || "—"}</TD>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── CONTRACT VIEW ────────────────────────────────────────────────────────────

const ContractTab = ({ contract }) => {
  if (!contract) return <EmptyState icon="📄" text="No contract data" />;

  const con = contract.capacityConsumer;
  const gen = contract.capacityGenerator;
  const connections = contract.bilateralConnections || [];

  const boolLabel = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
  const formatIso = (iso) => (iso ? iso.replace("T", "  ").slice(0, 19) : "—");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#1e293b",
            fontFamily: "monospace",
          }}
        >
          {contract.contractUniqueId || `Contract #${contract.id}`}
        </span>
        <StatusChip value={contract.status} />
        {contract.schedulingStatus &&
          contract.schedulingStatus !== contract.status && (
            <StatusChip value={contract.schedulingStatus} />
          )}
        <span
          style={{
            fontSize: 11,
            background: "#fff7ed",
            color: "#c2410c",
            border: "1px solid #fed7aa",
            borderRadius: 20,
            padding: "2px 10px",
            fontWeight: 700,
          }}
        >
          {contract.initiatorType?.toUpperCase()}
        </span>
      </div>

      {/* ── Overview ── */}
      <Section title="Contract Overview" accent="#6366f1">
        <Field label="Contract ID" value={contract.id} mono />
        <Field label="Unique ID" value={contract.contractUniqueId} mono />
        <Field label="Status">
          {" "}
          <StatusChip value={contract.status} />
        </Field>
        <Field label="Scheduling">
          {" "}
          <StatusChip value={contract.schedulingStatus} />
        </Field>
        <Field label="Initiator" value={contract.initiatorType} />
        <Field
          label="DAM Price"
          value={contract.damPrice != null ? `₹ ${contract.damPrice}` : "—"}
        />
        <Field
          label="Charges Finalised"
          value={boolLabel(contract.isChargesFinalized)}
        />
        <Field
          label="Scheduling Started"
          value={formatIso(contract.schedulingStartedAt)}
        />
      </Section>

      {/* ── Parties ── */}
      <Section title="Parties" accent="#0ea5e9" gridCols="1fr 1fr">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: "2px 0",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0369a1",
              letterSpacing: 0.5,
            }}
          >
            CONSUMER
          </div>
          <Field label="Name" value={contract.consumerName} />
          <Field label="Listing" value={contract.capacityConsumerName} mono />
          {con && (
            <>
              <Field label="Region" value={con.region} />
              <Field label="State" value={con.stateLabel} />
              <Field label="Voltage" value={con.voltageLevel} />
              <Field label="Delivery" value={con.deliveryType} />
            </>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: "2px 0",
            borderLeft: "1px solid #f1f5f9",
            paddingLeft: 18,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#15803d",
              letterSpacing: 0.5,
            }}
          >
            GENERATOR
          </div>
          <Field label="Name" value={contract.generatorName} />
          <Field label="Listing" value={contract.capacityGeneratorName} mono />
          {gen && (
            <>
              <Field label="Region" value={gen.region} />
              <Field label="State" value={gen.stateLabel} />
              <Field label="Voltage" value={gen.voltageLevel} />
              <Field label="Delivery" value={gen.deliveryType} />
            </>
          )}
        </div>
      </Section>

      {/* ── Financial ── */}
      <Section title="Financial" accent="#f59e0b">
        <Field
          label="Payment Security"
          value={
            contract.paymentSecurity != null
              ? String(contract.paymentSecurity)
              : "—"
          }
        />
        <Field
          label="Initial Margin"
          value={
            contract.initialMargin != null
              ? String(contract.initialMargin)
              : "—"
          }
        />
        <Field
          label="EMD"
          value={contract.emd != null ? String(contract.emd) : "—"}
        />
      </Section>

      {/* ── Documents & Acceptance ── */}
      <Section title="Documents & Acceptance" accent="#8b5cf6">
        <Field label="Con. Consent Doc" value={contract.conConsentDocId} mono />
        <Field label="Gen. Consent Doc" value={contract.genConsentDocId} mono />
        <Field
          label="Con. Agreement Doc"
          value={contract.conAgreementDocId}
          mono
        />
        <Field
          label="Gen. Agreement Doc"
          value={contract.genAgreementDocId}
          mono
        />
        <Field label="Con. LDC Doc" value={contract.conLdcDocument} mono />
        <Field label="Gen. LDC Doc" value={contract.genLdcDocument} mono />
        <Field
          label="Con. Agreement Acc."
          value={boolLabel(contract.conAgreementAcceptanceStatus)}
        />
        <Field
          label="Gen. Agreement Acc."
          value={boolLabel(contract.genAgreementAcceptanceStatus)}
        />
        <Field
          label="Con. Terms Acc."
          value={boolLabel(contract.conTermsAcceptanceStatus)}
        />
        <Field
          label="Gen. Terms Acc."
          value={boolLabel(contract.genTermsAcceptanceStatus)}
        />
      </Section>

      {/* ── Consumer Slots ── */}
      {con?.slots?.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#0369a1",
              padding: "10px 18px",
              borderBottom: "1px solid #f1f5f9",
              background: "#f0f9ff",
              borderLeft: "3px solid #0ea5e9",
            }}
          >
            Consumer Slots — {con.name}{" "}
            <span style={{ fontWeight: 400, fontSize: 10, color: "#64748b" }}>
              ({con.slots.length} slots)
            </span>
          </div>
          <div style={{ padding: "10px 18px 14px" }}>
            <SlotsTable
              slots={con.slots}
              costKey="landingCost"
              energyKey="purchaseEnergy"
            />
          </div>
        </div>
      )}

      {/* ── Generator Slots ── */}
      {gen?.slots?.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#15803d",
              padding: "10px 18px",
              borderBottom: "1px solid #f1f5f9",
              background: "#f0fdf4",
              borderLeft: "3px solid #22c55e",
            }}
          >
            Generator Slots — {gen.name}{" "}
            <span style={{ fontWeight: 400, fontSize: 10, color: "#64748b" }}>
              ({gen.slots.length} slots)
            </span>
          </div>
          <div style={{ padding: "10px 18px 14px" }}>
            <SlotsTable
              slots={gen.slots}
              costKey="sellingCost"
              energyKey="sellEnergy"
            />
          </div>
        </div>
      )}

      {/* ── Bilateral Connections ── */}
      {connections.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#6366f1",
              padding: "10px 18px",
              borderBottom: "1px solid #f1f5f9",
              background: "#f5f3ff",
              borderLeft: "3px solid #6366f1",
            }}
          >
            Bilateral Connections{" "}
            <span style={{ fontWeight: 400, fontSize: 10, color: "#64748b" }}>
              ({connections.length} connections)
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "60px 1fr 1fr 80px 80px 90px 90px 90px 90px",
              }}
            >
              {[
                "ID",
                "Date Start",
                "Date End",
                "Slot Start",
                "Slot End",
                "Qty (MW)",
                "Amount ₹",
                "Rate Type",
                "Status",
              ].map((h) => (
                <TH key={h} style={{ fontSize: 9 }}>
                  {h}
                </TH>
              ))}
              {connections.map((conn, i) => (
                <React.Fragment key={i}>
                  <TD style={{ fontSize: 11 }}>{conn.id}</TD>
                  <TD style={{ fontSize: 11 }}>{conn.dateStartAt}</TD>
                  <TD style={{ fontSize: 11 }}>{conn.dateEndAt}</TD>
                  <TD style={{ fontSize: 11 }}>
                    {fmtSlotTime(conn.slotStartAt)}
                  </TD>
                  <TD style={{ fontSize: 11 }}>
                    {fmtSlotTime(conn.slotEndAt)}
                  </TD>
                  <TD style={{ fontSize: 11 }}>
                    {conn.quantity != null ? conn.quantity : "—"}
                  </TD>
                  <TD style={{ fontSize: 11 }}>
                    {conn.amount != null ? `₹ ${conn.amount}` : "—"}
                  </TD>
                  <TD style={{ fontSize: 11 }}>{conn.energyRateType || "—"}</TD>
                  <TD chip>{conn.status}</TD>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── LISTING VIEW ─────────────────────────────────────────────────────────────

const ListingTab = ({ listing, entityType, capacityId }) => {
  const name =
    listing?.consumerName || listing?.generatorName || listing?.conName;
  const formatDateTime = (date, time) =>
    date ? `${date}${time ? "  " + time : ""}` : "—";
  const formatIso = (iso) => (iso ? iso.replace("T", "  ").slice(0, 19) : "—");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
          {listing?.name || `Capacity #${capacityId}`}
        </span>
        <StatusChip value={listing?.status} />
        {listing?.listingMode && (
          <span
            style={{
              fontSize: 11,
              background: "#ede9fe",
              color: "#6d28d9",
              border: "1px solid #c4b5fd",
              borderRadius: 20,
              padding: "2px 10px",
              fontWeight: 700,
            }}
          >
            {listing.listingMode}
          </span>
        )}
        {entityType && (
          <span
            style={{
              fontSize: 11,
              background: "#e0f2fe",
              color: "#0369a1",
              border: "1px solid #7dd3fc",
              borderRadius: 20,
              padding: "2px 10px",
              fontWeight: 700,
            }}
          >
            {entityType}
          </span>
        )}
      </div>

      <Section title="Basic Information" accent="#6366f1">
        <Field label="Capacity ID" value={capacityId} mono />
        <Field label="Listing Name" value={listing?.name} mono />
        <Field label="Entity Name" value={name} />
        <Field label="Listing Type" value={listing?.listingType} />
        <Field
          label="Is Interstate"
          value={
            listing?.isInterstate != null
              ? listing.isInterstate
                ? "Yes"
                : "No"
              : "—"
          }
        />
        <Field
          label="Is Bilateral"
          value={
            listing?.isBilateral != null
              ? listing.isBilateral
                ? "Yes"
                : "No"
              : "—"
          }
        />
        <Field
          label="Is Intra Day"
          value={
            listing?.isIntraDay != null
              ? listing.isIntraDay
                ? "Yes"
                : "No"
              : "—"
          }
        />
        <Field
          label="Trader Assigned"
          value={
            listing?.traderAssigned != null
              ? String(listing.traderAssigned)
              : "—"
          }
        />
      </Section>

      <Section title="Energy Details" accent="#0ea5e9">
        <Field label="Energy Source" value={listing?.energySourceType} />
        <Field label="Energy Fuel" value={listing?.energyFuelType} />
        <Field label="Voltage Level" value={listing?.voltageLevel} />
        <Field label="Region" value={listing?.region} />
        <Field label="State Label" value={listing?.stateLabel} />
        <Field label="Matching Tags" value={listing?.matchingTags} />
      </Section>

      <Section title="Dates & Expiry" accent="#f59e0b">
        <Field
          label="Listing Exp."
          value={formatDateTime(
            listing?.listingExpDate,
            listing?.listingExpTime,
          )}
        />
        <Field
          label="Terms Exp."
          value={formatDateTime(listing?.termsExpDate, listing?.termsExpTime)}
        />
        <Field label="Created At" value={formatIso(listing?.createdAt)} />
      </Section>

      <Section title="Financial" accent="#22c55e">
        <Field
          label="Payment Security"
          value={
            listing?.paymentSecurity != null
              ? String(listing.paymentSecurity)
              : "—"
          }
        />
        <Field
          label="Initial Margin"
          value={
            listing?.initialMargin != null ? String(listing.initialMargin) : "—"
          }
        />
        <Field
          label="EMD"
          value={listing?.emd != null ? String(listing.emd) : "—"}
        />
        <Field
          label="LC Version"
          value={
            listing?.lcVersionId != null ? String(listing.lcVersionId) : "—"
          }
        />
      </Section>

      {listing?.slots?.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#64748b",
              padding: "10px 18px",
              borderBottom: "1px solid #f1f5f9",
              background: "#f8fafc",
            }}
          >
            Slots ({listing.slots.length})
          </div>
          <div style={{ padding: "10px 18px 14px" }}>
            <SlotsTable
              slots={listing.slots}
              costKey="landingCost"
              energyKey="purchaseEnergy"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ContractDetailPopup = ({ contract, onClose }) => {
  const boolLabel = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
  if (!contract) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          width: "min(760px, 96vw)",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#6d28d9",
                fontFamily: "monospace",
              }}
            >
              {contract.contractUniqueId || `#${contract.id}`}
            </span>
            <StatusChip value={contract.status} />
            {contract.schedulingStatus &&
              contract.schedulingStatus !== contract.status && (
                <StatusChip value={contract.schedulingStatus} />
              )}
            {contract.initiatorType && (
              <span
                style={{
                  fontSize: 11,
                  background: "#fff7ed",
                  color: "#c2410c",
                  border: "1px solid #fed7aa",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {contract.initiatorType}
              </span>
            )}
            {(contract.consumerName || contract.generatorName) && (
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {contract.consumerName} ↔ {contract.generatorName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              border: "none",
              borderRadius: 8,
              background: "#f1f5f9",
              cursor: "pointer",
              fontSize: 16,
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* Contract Overview */}
          <Section title="Contract Overview" accent="#6366f1">
            <Field label="Consumer" value={contract.consumerName} />
            <Field label="Generator" value={contract.generatorName} />
            <Field
              label="Consumer Listing"
              value={contract.capacityConsumerName}
              mono
            />
            <Field
              label="Generator Listing"
              value={contract.capacityGeneratorName}
              mono
            />
            <Field label="Scheduling Status">
              <StatusChip value={contract.schedulingStatus} />
            </Field>
            <Field
              label="DAM Price"
              value={contract.damPrice != null ? `₹ ${contract.damPrice}` : "—"}
            />
            <Field
              label="Charges Finalised"
              value={boolLabel(contract.isChargesFinalized)}
            />
          </Section>

          {/* Financial Summary */}
          <Section title="Financial Summary" accent="#22c55e">
            <Field
              label="Initial Margin (%)"
              value={
                contract.initialMargin != null
                  ? `${contract.initialMargin}%`
                  : "—"
              }
            />
            <Field
              label="EMD (₹)"
              value={contract.emd != null ? `₹ ${contract.emd}` : "—"}
            />
            <Field label="Payment Security" value={contract.paymentSecurity} />
            <Field
              label="Transaction ID"
              value={contract.transactionId || "—"}
              mono
            />
            <Field
              label="Con Agreement"
              value={
                contract.conAgreementAcceptanceStatus != null
                  ? contract.conAgreementAcceptanceStatus
                    ? "Accepted"
                    : "Pending"
                  : "—"
              }
            />
            <Field
              label="Gen Agreement"
              value={
                contract.genAgreementAcceptanceStatus != null
                  ? contract.genAgreementAcceptanceStatus
                    ? "Accepted"
                    : "Pending"
                  : "—"
              }
            />
          </Section>

          {/* Documents (only if any doc IDs present) */}
          {(contract.conConsentDocId ||
            contract.genConsentDocId ||
            contract.conAgreementDocId ||
            contract.genAgreementDocId) && (
            <Section title="Documents & Acceptance" accent="#8b5cf6">
              <Field
                label="Con. Consent Doc"
                value={contract.conConsentDocId}
                mono
              />
              <Field
                label="Gen. Consent Doc"
                value={contract.genConsentDocId}
                mono
              />
              <Field
                label="Con. Agreement Doc"
                value={contract.conAgreementDocId}
                mono
              />
              <Field
                label="Gen. Agreement Doc"
                value={contract.genAgreementDocId}
                mono
              />
              <Field
                label="Con. Terms Acc."
                value={boolLabel(contract.conTermsAcceptanceStatus)}
              />
              <Field
                label="Gen. Terms Acc."
                value={boolLabel(contract.genTermsAcceptanceStatus)}
              />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

const ListingContractsTab = ({ contracts }) => {
  const [selected, setSelected] = React.useState(null);

  if (!contracts?.length)
    return <EmptyState icon="📄" text="No contracts found for this listing" />;

  return (
    <>
      {selected && (
        <ContractDetailPopup
          contract={selected}
          onClose={() => setSelected(null)}
        />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {contracts.map((row, i) => (
          <div
            key={row.id ?? i}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              overflow: "hidden",
              transition: "box-shadow 0.15s, border-color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#f97316aa";
              e.currentTarget.style.boxShadow =
                "0 2px 12px rgba(249,115,22,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {/* Card header row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                background: "#fafafa",
                borderBottom: "1px solid #f1f5f9",
                flexWrap: "wrap",
              }}
            >
              {/* Contract ID */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#6d28d9",
                  fontFamily: "monospace",
                  letterSpacing: "0.02em",
                }}
              >
                {row.contractUniqueId || `#${row.id}` || "—"}
              </span>

              {/* Status */}
              <StatusChip value={row.status} />

              {/* Scheduling — only if different from status */}
              {row.schedulingStatus && row.schedulingStatus !== row.status && (
                <StatusChip value={row.schedulingStatus} />
              )}

              {/* Initiator badge */}
              {(row.initiatorType || row.type) && (
                <span
                  style={{
                    fontSize: 10,
                    background: "#fff7ed",
                    color: "#c2410c",
                    border: "1px solid #fed7aa",
                    borderRadius: 20,
                    padding: "2px 9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {row.initiatorType ?? row.type}
                </span>
              )}

              {/* Amount — pushed right */}
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#1e293b",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {row.amount != null
                  ? `₹ ${row.amount}`
                  : row.damPrice != null
                    ? `₹ ${row.damPrice}`
                    : "—"}
              </span>

              {/* View button */}
              <button
                onClick={() => setSelected(row)}
                title="View details"
                style={{
                  border: "1px solid #fed7aa",
                  background: "#fff7ed",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  color: "#f97316",
                  padding: 0,
                  transition: "all 0.15s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ffedd5";
                  e.currentTarget.style.borderColor = "#f97316";
                  e.currentTarget.style.color = "#c2410c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fff7ed";
                  e.currentTarget.style.borderColor = "#fed7aa";
                  e.currentTarget.style.color = "#f97316";
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>

            {/* Detail grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "10px 20px",
                padding: "12px 16px",
              }}
            >
              <Field label="Consumer" value={row.consumerName} />
              <Field label="Generator" value={row.generatorName} />
              <Field
                label="Consumer Listing"
                value={row.capacityConsumerName}
                mono
              />
              <Field
                label="Generator Listing"
                value={row.capacityGeneratorName}
                mono
              />
              {row.schedulingStatus === row.status && row.schedulingStatus && (
                <Field label="Scheduling">
                  <StatusChip value={row.schedulingStatus} />
                </Field>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const AuditListingDetailView = ({ data }) => {
  const isContractResponse = !!data?.contract;

  const contractTabs = [
    { id: "contract", label: "Contract", accent: "#6366f1", count: null },
    {
      id: "revision",
      label: "Revision History",
      accent: "#0ea5e9",
      count: data?.revisionHistory?.length ?? 0,
    },
  ];

  const listingTabs = [
    { id: "listing", label: "Listing", accent: "#8b5cf6", count: null },
    {
      id: "contracts",
      label: "Contracts",
      accent: "#f97316",
      count: data?.contracts?.length ?? 0,
    },
    {
      id: "revision",
      label: "Revision History",
      accent: "#0ea5e9",
      count: data?.revisionHistory?.length ?? 0,
    },
  ];

  const tabs = isContractResponse ? contractTabs : listingTabs;
  const [tab, setTab] = useState(tabs[0].id);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <TabBar tabs={tabs} active={tab} onSelect={setTab} />

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {isContractResponse ? (
          <>
            {tab === "contract" && <ContractTab contract={data.contract} />}
            {tab === "revision" && (
              <RevisionTab revisionHistory={data.revisionHistory} />
            )}
          </>
        ) : (
          <>
            {tab === "listing" && (
              <ListingTab
                listing={data?.consumerListing || data?.generatorListing}
                entityType={data?.entityType}
                capacityId={data?.capacityId}
              />
            )}
            {tab === "contracts" && (
              <ListingContractsTab contracts={data?.contracts} />
            )}
            {tab === "revision" && (
              <RevisionTab revisionHistory={data?.revisionHistory} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditListingDetailView;
